import { Building2, Check, ChevronsUpDown, Plus } from "lucide-react";
import type { DashboardWorkspace } from "@/api/types";
import { getInitials } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";

interface DashboardWorkspaceSwitcherProps {
  businessName: string;
  workspaces: DashboardWorkspace[];
  collapsed?: boolean;
  bottomNav?: boolean;
  switching?: boolean;
  onSelect: (workspace: DashboardWorkspace) => void;
  onListNewBusiness: () => void;
}

function formatRoleLabel(role: string): string {
  return role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function WorkspaceMark({ name }: { name: string }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-lime text-[11px] font-bold text-[#1A1A1E]">
      {getInitials(name)}
    </span>
  );
}

function DashboardWorkspaceSwitcher({
  businessName,
  workspaces,
  collapsed = false,
  bottomNav = false,
  switching = false,
  onSelect,
  onListNewBusiness,
}: DashboardWorkspaceSwitcherProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "min-w-0 border border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime/70",
            bottomNav
              ? "mx-1 flex w-full flex-col items-center gap-0.5 rounded-md py-1 text-pro-text-faint hover:bg-pro-surface hover:text-pro-text"
              : "flex items-center gap-2 rounded-[14px] text-left text-pro-text hover:border-pro-border hover:bg-pro-surface",
            !bottomNav && (collapsed ? "size-10 justify-center p-0" : "w-full px-2.5 py-2"),
          )}
          disabled={switching}
          aria-label="Switch business"
        >
          {bottomNav ? (
            <>
              <Building2 className="size-5" />
              <span className="text-[10px] font-medium">Business</span>
            </>
          ) : (
            <>
              <WorkspaceMark name={businessName} />
              {!collapsed && (
                <>
                  <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">
                    {businessName}
                  </span>
                  <ChevronsUpDown className="size-4 shrink-0 text-pro-text-faint" />
                </>
              )}
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={bottomNav ? "end" : collapsed ? "center" : "start"}
        side={bottomNav ? "top" : collapsed ? "right" : "bottom"}
        className="w-[min(320px,calc(100vw-24px))] border-pro-border bg-pro-bg p-1.5 text-pro-text shadow-pro-card"
      >
        {workspaces.map((workspace) => (
          <DropdownMenuItem
            key={workspace.id}
            disabled={workspace.selected || switching}
            onSelect={() => onSelect(workspace)}
            className={cn(
              "items-center gap-2 rounded-[10px] px-2.5 py-2.5 focus:bg-pro-surface",
              workspace.selected && "bg-pro-surface text-pro-text",
            )}
          >
            <WorkspaceMark name={workspace.name} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold">{workspace.name}</span>
              <span className="block truncate text-[10px] text-pro-text-muted">
                {formatRoleLabel(workspace.role)}
              </span>
            </span>
            {workspace.selected && <Check className="size-4 shrink-0 text-lime" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-pro-border" />
        <DropdownMenuItem
          onSelect={onListNewBusiness}
          className="rounded-[10px] px-2.5 py-2.5 text-[13px] font-semibold focus:bg-pro-surface"
        >
          <Plus className="size-4 text-lime" />
          <span>List new business</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { DashboardWorkspaceSwitcher };
